import { useState } from 'react'

import { Button, CardContent, CardHeader, Link, TextField } from '@/components'
import { useAuthMutations } from '@/modules/auth'

interface OtpFormProps {
  email: string
  onReturn: () => void
  onCodeVerified: () => void
  isLogin?: boolean
}

export const OtpForm = ({
  email,
  onReturn,
  onCodeVerified,
  isLogin,
}: OtpFormProps) => {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { verifyOtp } = useAuthMutations()
  const label = isLogin ? 'login' : 'sign up'

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    verifyOtp({ code, email })
      .then(() => onCodeVerified())
      .catch(() => {
        setError('Inserted code is not valid and/or expired')
        setIsLoading(false)
      })
  }

  return (
    <>
      <CardHeader className="space-y-2 pb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-center">
          Check your email
        </h1>
        <p className="text-sm text-muted-foreground text-center leading-relaxed">
          You'll receive a {label} code at{' '}
          <span className="font-medium text-foreground">{email}</span>.
        </p>
        <p className="text-xs text-muted-foreground text-center">
          The code will be valid for 15 minutes.
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={onSubmit} className="space-y-6 text-center">
          <TextField
            label="Enter verification code"
            helpText="If you don't see it, please check your spam or junk folder."
            id="code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="000000"
            value={code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value.replace(/\D/g, '')
              setCode(value)
            }}
            disabled={isLoading}
            required
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            inputClassName="h-16 text-center text-3xl font-mono tracking-[0.5em] font-semibold border-2 focus-visible:ring-2"
            error={error}
          />

          <div className="space-y-3">
            <Button
              type="submit"
              className="w-full h-10 font-medium"
              isLoading={isLoading}
              disabled={code.length !== 6}
              loadingText="Verifying..."
            >
              Verify and continue
            </Button>

            <Link asChild>
              <button
                type="button"
                onClick={onReturn}
                className="text-sm font-medium"
              >
                Use different email
              </button>
            </Link>
          </div>
        </form>
      </CardContent>
    </>
  )
}
